import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Clock, FileWarning, Inbox, Upload, Sparkles, ArrowRight } from 'lucide-react';
import { AppShell } from '../../components/AppShell';
import { Card } from '../../components/ui/Card';
import { Pill } from '../../components/ui/Pill';
import { SignalRow } from '../../components/SignalFeed';
import { InboxInvoiceDrawer } from '../../components/InboxInvoiceDrawer';
import { useApInvoices, useInboxCounts, useApproveApInvoice, useUploadApInvoice } from '../../hooks/useApInvoices';
import { useSignals } from '../../hooks/useSignals';
import { useWorkspace } from '../../hooks/useWorkspace';
import { formatAmount, formatDate } from '../../lib/format';

export function InboxPage() {
  const { wsId } = useParams<{ wsId: string }>();
  const { t, i18n } = useTranslation();
  const { data: ws } = useWorkspace(wsId);
  const { data: counts } = useInboxCounts(wsId);
  const { data: signals } = useSignals(wsId);
  const topSignals = signals?.slice(0, 3) ?? [];
  const { data: invoices, isLoading } = useApInvoices(wsId, { status: 'pending_review' });
  const approve = useApproveApInvoice(wsId);
  const upload = useUploadApInvoice(wsId);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const [drawerId, setDrawerId] = useState<string | null>(null);

  const handleFile = async (file: File) => {
    setUploadErr(null);
    try {
      await upload.mutateAsync(file);
    } catch (err: any) {
      setUploadErr(err?.response?.data?.message ?? err?.message ?? 'Upload failed');
    }
  };

  return (
    <AppShell>
      <div className="px-8 py-7 max-w-7xl">
        <div className="flex items-center justify-between mb-2">
          <h1 className="display text-2xl font-semibold">{t('inbox.title')}</h1>
          <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-ink-300 dark:border-ink-700 hover:bg-ink-50 dark:hover:bg-ink-900 cursor-pointer text-sm">
            <Upload size={14} />
            {t('inbox.uploadInvoice')}
            <input
              type="file"
              hidden
              accept="application/pdf,image/*,text/html,text/plain,.pdf,.html,.htm,.txt"
              onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
            />
          </label>
        </div>
        <p className="text-sm text-ink-500 mb-6">{t('inbox.subtitle')}</p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <CountCard icon={Inbox} label={t('inbox.pendingReview')} value={counts?.pendingReview ?? 0} tone="accent" />
          <CountCard icon={Clock} label={t('inbox.dueSoon')} value={counts?.dueSoon ?? 0} tone="neutral" />
          <CountCard icon={AlertCircle} label={t('inbox.pastDue')} value={counts?.pastDue ?? 0} tone="warn" />
          <CountCard icon={FileWarning} label={t('inbox.disputed')} value={counts?.disputed ?? 0} tone="muted" />
        </div>

        {topSignals.length > 0 && (
          <Card className="mb-6">
            <div className="px-5 py-3 border-b border-ink-200 dark:border-ink-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles size={15} className="text-accent-600 dark:text-accent-300" />
                <span className="text-sm font-semibold">{t('home.nextBestAction')}</span>
              </div>
              <Link
                to={`/app/workspace/${wsId}/home`}
                className="inline-flex items-center gap-1 text-xs text-accent-700 dark:text-accent-200 hover:underline"
              >
                {t('home.viewAll')}
                <ArrowRight size={12} />
              </Link>
            </div>
            <div className="divide-y divide-ink-200 dark:divide-ink-800">
              {topSignals.map((s) => (
                <SignalRow key={s.id} signal={s} compact />
              ))}
            </div>
          </Card>
        )}

        {ws ? (
          <Card className="p-4 mb-6 bg-accent-50 dark:bg-accent-900/20 border-accent-200 dark:border-accent-800">
            <div className="text-xs text-ink-500">{t('inbox.inboxAddressLabel')}</div>
            <div className="font-mono text-sm mt-0.5">{ws.inbox_alias}@in.plyez.com</div>
          </Card>
        ) : null}

        {uploadErr && <div className="mb-4 text-sm text-warn-600">{uploadErr}</div>}

        <Card>
          {isLoading ? (
            <div className="p-10 text-center text-ink-500">{t('common.loading')}</div>
          ) : !invoices || invoices.length === 0 ? (
            <div className="p-10 text-center text-ink-500">{t('inbox.empty')}</div>
          ) : (
            <ul className="divide-y divide-ink-200 dark:divide-ink-800">
              {invoices.map((inv) => (
                <li key={inv.id}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setDrawerId(inv.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter') setDrawerId(inv.id); }}
                    className="w-full px-5 py-4 flex items-start justify-between gap-4 text-left cursor-pointer hover:bg-ink-50 dark:hover:bg-ink-900 focus:bg-ink-50 dark:focus:bg-ink-900 focus:outline-none transition-colors"
                  >
                    <div className="min-w-0">
                      <div className="font-medium truncate">
                        {inv.invoice_number ?? t('inbox.noInvoiceNumber')}
                      </div>
                      <div className="text-xs text-ink-500 mt-0.5 flex flex-wrap items-center gap-2">
                        <span>{formatDate(inv.issue_date, i18n.language)} → {formatDate(inv.due_date, i18n.language)}</span>
                        {inv.jp_qualified && (
                          <Pill tone="success">{t('vendors.qualifiedActive')} {inv.jp_invoice_registration}</Pill>
                        )}
                        {inv.ocr_confidence && (
                          <Pill tone="muted">{t('inbox.ocrConfidence', { percent: (Number(inv.ocr_confidence) * 100).toFixed(0) })}</Pill>
                        )}
                        <span className="font-mono text-[10px] uppercase">{inv.source}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                      <div className="text-right">
                        <div className="num text-lg font-semibold">{formatAmount(inv.total_cents, inv.currency, i18n.language)}</div>
                        <div className="text-xs text-ink-500">{inv.currency}</div>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); approve.mutate(inv.id); }}
                        disabled={approve.isPending}
                        className="px-3 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50"
                      >
                        {t('common.approve')}
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
      {wsId && (
        <InboxInvoiceDrawer
          wsId={wsId}
          invoiceId={drawerId}
          open={!!drawerId}
          onOpenChange={(o) => !o && setDrawerId(null)}
        />
      )}
    </AppShell>
  );
}

function CountCard({ icon: Icon, label, value, tone }: { icon: typeof Inbox; label: string; value: number; tone: 'accent' | 'neutral' | 'warn' | 'muted' }) {
  const tones = {
    accent: 'bg-accent-50 dark:bg-accent-900/40 text-accent-700 dark:text-accent-200',
    neutral: 'bg-ink-100 dark:bg-ink-800 text-ink-700 dark:text-ink-200',
    warn: 'bg-amber-50 dark:bg-warn-600/20 text-warn-600',
    muted: 'bg-ink-50 dark:bg-ink-900 text-ink-500',
  } as const;
  return (
    <Card className="p-4 flex items-center gap-3">
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${tones[tone]}`}>
        <Icon size={16} />
      </div>
      <div>
        <div className="text-xs text-ink-500">{label}</div>
        <div className="num text-xl font-semibold">{value}</div>
      </div>
    </Card>
  );
}
